import { useState, useEffect, useRef } from "react";
import { auth } from "./firebase";
import { getTranslationCache, setTranslationCache, setExamples23Cache, saveReport, getCurriculumCorrections } from "./firebase";
import { useT } from "./i18n";
import { askHaiku, askClaude, translateLessonWords, translateExamples23 } from "./api";
import { ACTIVELY_TRANSLATED_LANGS } from "./constants";
import { makeFSRSCard } from "./fsrs";
import { save, load, getTranslatedTopicName } from "./utils";
import { speak, useWakeLock, getSpeakLangCode } from "./tts";
import { createActivityTimer } from "./activityTimer";
import { getCurriculum, getTopicCount } from "./curriculum";

const MAX_EXAMPLES = 2;
export const LESSON_POSITION_KEY = "lastLessonPosition";

export function LessonView({ profile, cards, topic, completedTopics, onComplete, onBack, destLang = "turkish", onGoToListenSpeak }) {
  useWakeLock();
  const { t, langName } = useT();
  const baseLanguage = profile?.baseLanguage || "English";
  const langCode = (profile?.targetLanguage || "Turkish").toLowerCase();
  const curriculum = getCurriculum(langCode);
  const topicData = topic || curriculum[0];
  const speakLang = getSpeakLangCode(langCode);

  const [dialogue, setDialogue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogueCount, setDialogueCount] = useState(0);
  const [done, setDone] = useState(false);
  // Lazy-init once per lesson mount (not useState/useMemo — the timer's internal
  // listeners must survive for the component's whole lifetime, not be recreated).
  const timerRef = useRef(null);
  if (timerRef.current === null) timerRef.current = createActivityTimer();
  useEffect(() => () => timerRef.current?.stop(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [translatedWords, setTranslatedWords] = useState(null);
  const [translating, setTranslating] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const cacheKeyLogLabel = `${langCode}_${topicData.topic_id}_${baseLanguage}`;

    // Lazy fill-in for example_2/3 translations (ex2Tr/ex3Tr). Runs in the background AFTER the
    // lesson is already shown with the word-meaning fallback — never blocks initial render, since
    // it's a nice-to-have on top of an already-usable lesson, not something the user is waiting
    // on. `cachedWords` is the FULL positional words array as read from (or just written to) the
    // translations cache doc; `sourceWords` is the corresponding Turkish source (correctedWords)
    // the translation is generated FROM. One batched Claude call covers every word in the lesson
    // that's still missing a translation — already-backfilled words (today: English, Farsi, via
    // scripts/prewarm-translations.js --fields=examples23) are left untouched and cost nothing.
    function lazyFillExamples23(cachedWords, sourceWords, lang) {
      if (!ACTIVELY_TRANSLATED_LANGS.includes(lang)) return;
      const missingIdx = cachedWords
        .map((w, i) => i)
        .filter(i => !(cachedWords[i] && typeof cachedWords[i].ex2Tr === "string" && typeof cachedWords[i].ex3Tr === "string"));
      if (missingIdx.length === 0) return;
      const items = missingIdx.map(i => sourceWords[i]);
      translateExamples23(items, lang, topicData.topic, destLang)
        .then(results => {
          // The user may have navigated to a different lesson/language before this (extra,
          // non-blocking) call resolved — bail out rather than writing translations for the
          // wrong topic onto whatever's now displayed. The Firestore write below is still safe
          // to skip too: a superseded effect's fill will simply be redone by the next visit.
          if (cancelled) return;
          const mergedWords = cachedWords.slice();
          missingIdx.forEach((wordIdx, k) => {
            mergedWords[wordIdx] = { ...mergedWords[wordIdx], ex2Tr: results[k]?.ex2Tr ?? "", ex3Tr: results[k]?.ex3Tr ?? "", ex23Source: "lazy" };
          });
          setExamples23Cache(langCode, topicData.topic_id, lang, mergedWords).catch(() => {});
          setTranslatedWords(prev => prev && prev.map((w, i) => missingIdx.includes(i)
            ? { ...w, example_2_translation: mergedWords[i].ex2Tr || w.example_2_translation, example_3_translation: mergedWords[i].ex3Tr || w.example_3_translation }
            : w));
        })
        .catch(err => console.error(`Lazy examples23 fill failed for ${langCode}_${topicData.topic_id}_${lang}`, err));
    }

    // Live curriculum corrections (see admin's "Fix Content" action / applyContentCorrection)
    // are fetched and merged onto topicData.words BEFORE any cache-check/translate logic below
    // runs, so a corrected Turkish word/example is what actually gets cached and shown — one
    // query per topic load, not per word, and a no-op (empty array) for the common case of a
    // topic with no corrections. Merged by word_id; array order/length is never changed, so the
    // Layer 2 cache's positional (not word_id-keyed) indexing stays valid.
    getCurriculumCorrections(topicData.topic_id, langCode)
      .then(corrections => {
        const correctionByWordId = new Map(corrections.map(c => [c.word_id, c]));
        const correctedWords = topicData.words.map(w => {
          const c = correctionByWordId.get(w.word_id);
          if (!c) return w;
          return {
            ...w,
            turkish: c.turkish ?? w.turkish,
            english: c.meaning ?? w.english,
            example: c.example ?? w.example,
          };
        });
        // Passing correctedWords (not the raw static topicData.words) into get/setTranslationCache
        // means an admin content correction busts the cached content-hash too, same as a
        // curriculum.js edit does — both are just "the content changed since this was cached".
        return getTranslationCache(langCode, topicData.topic_id, baseLanguage, correctedWords)
          .then(cached => {
            if (cached) {
              setTranslatedWords(correctedWords.map((w, i) => ({
                ...w, english: cached[i]?.meaning || w.english,
                example_translation: cached[i]?.exTr || w.example_translation,
                // ex2Tr/ex3Tr may not exist yet for every topic+language — see the lazy
                // fill-in kicked off below, which patches translatedWords in place once it
                // resolves. Until then this falls back to curriculum.js's own null
                // placeholder, which VocabCard's "Another Example" flow treats as "no cached
                // translation, show the word's meaning instead".
                example_2_translation: cached[i]?.ex2Tr || w.example_2_translation,
                example_3_translation: cached[i]?.ex3Tr || w.example_3_translation,
              })));
              lazyFillExamples23(cached, correctedWords, baseLanguage);
            } else {
              return translateLessonWords(correctedWords, baseLanguage, topicData.topic, destLang)
                .then(translations => {
                  setTranslationCache(langCode, topicData.topic_id, baseLanguage, correctedWords, { topicId: topicData.topic_id, lang: baseLanguage, words: translations }).catch(err => console.error(`Caching translation failed for ${langCode}_${topicData.topic_id}_${baseLanguage} (shown to the user regardless, just won't persist)`, err));
                  setTranslatedWords(correctedWords.map((w, i) => ({
                    ...w, english: translations[i]?.meaning || w.english,
                    example_translation: translations[i]?.exTr || w.example_translation,
                    // translateLessonWords only ever produces meaning/exTr (example_1) — a
                    // brand-new cache doc has no ex2Tr/ex3Tr yet, so this starts out as the
                    // curriculum null placeholder until the lazy fill-in below resolves.
                    example_2_translation: w.example_2_translation,
                    example_3_translation: w.example_3_translation,
                  })));
                  lazyFillExamples23(translations, correctedWords, baseLanguage);
                });
            }
          });
      })
      .catch((err) => {
        console.error(`Layer 2 translation (or curriculum corrections) unavailable for ${cacheKeyLogLabel}, falling back to untranslated words`, err);
        setTranslatedWords(topicData.words);
      })
      .finally(() => setTranslating(false));
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicData.order, baseLanguage]);

  const displayWords = translatedWords ?? topicData.words;
  const topicWords = topicData.words;
  const alreadyLearned = new Set(cards.map(c => c.id));

  const newWords = displayWords
    .map((w, i) => ({ ...w, id: w.word_id || `t${topicData.order}_${i}`, meaning: w.english }))
    .filter(w => !alreadyLearned.has(w.id));
  const reviewWords = displayWords
    .map((w, i) => ({ ...w, id: w.word_id || `t${topicData.order}_${i}`, meaning: w.english }))
    .filter(w => alreadyLearned.has(w.id));
  // A topic can end up with every word already carrying a flashcard without ever having
  // been marked complete — e.g. the placement test used to seed cards for correctly-answered
  // words across the whole curriculum (removed 2026-09-30, but those cards still exist on
  // older accounts), bypassing handleComplete below. Without this check
  // such a topic would show "Already Taken" forever while never appearing done/green in
  // All Lessons, since completedTopics is the only source AllLessonsView reads.
  const alreadyCompletedTopic = !!completedTopics?.[topicData.topic_id];

  // When a topic has no new words left (already fully completed), the focus track
  // walks through reviewWords instead so revisiting a completed lesson still gets
  // the single-word view rather than falling back to the legacy grid. Also switches to
  // reviewWords when lastLessonPosition (set by Global Search's word-match handler, or a
  // normal resume) targets a word that's already-learned within an otherwise-partially-
  // completed topic — that word only exists in reviewWords, so the newWords track alone
  // can't show it.
  const lessonPos = load(LESSON_POSITION_KEY);
  const posTargetsReviewWord = !!(
    lessonPos && lessonPos.topicId === topicData.topic_id &&
    !newWords.some(w => w.word_id === lessonPos.wordId) &&
    reviewWords.some(w => w.word_id === lessonPos.wordId)
  );
  const trackWords = (newWords.length > 0 && !posTargetsReviewWord) ? newWords : reviewWords;

  const [currentIndex, setCurrentIndex] = useState(() => {
    if (lessonPos && lessonPos.topicId === topicData.topic_id) {
      const idx = trackWords.findIndex(w => w.word_id === lessonPos.wordId);
      if (idx >= 0) return idx;
    }
    return 0;
  });
  const [peekingWordId, setPeekingWordId] = useState(null);
  const currentCardRef = useRef(null);

  useEffect(() => {
    if (translating || trackWords.length === 0) return;
    const w = trackWords[Math.min(currentIndex, trackWords.length - 1)];
    if (w) save(LESSON_POSITION_KEY, { topicId: topicData.topic_id, wordId: w.word_id });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, topicData.topic_id, translating, trackWords.length]);

  useEffect(() => {
    if (currentCardRef.current) {
      currentCardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [currentIndex]);

  const goNextWord = () => { setPeekingWordId(null); setCurrentIndex(i => Math.min(i + 1, trackWords.length - 1)); };
  const goPreviousWord = () => { setPeekingWordId(null); setCurrentIndex(i => Math.max(i - 1, 0)); };
  const togglePeek = (wordId) => setPeekingWordId(prev => prev === wordId ? null : wordId);
  const handlePeekCardClick = (e) => {
    if (e.target.closest("button")) return;
    setPeekingWordId(null);
  };

  const generateDialogue = () => {
    const wordList = topicWords.slice(0, 8).map(w => w.turkish).join(", ");
    const dialogueBaseLanguage = profile?.baseLanguage || "English";
    const sys = `You are Babi, a Turkish language tutor. The learner's base language is ${dialogueBaseLanguage}.
Generate a SHORT natural dialogue (4-6 lines) using some of these words from the topic "${topicData.topic}": ${wordList}.
Respond ONLY with valid JSON, no other text:
{"lines":[{"speaker":"A","text":"Turkish text","translation":"${dialogueBaseLanguage} translation"},{"speaker":"B","text":"Turkish text","translation":"${dialogueBaseLanguage} translation"}]}
Every "translation" value MUST be written in ${dialogueBaseLanguage}, not English (unless ${dialogueBaseLanguage} is English) — never leave it in English as a fallback.
Use at most 6 lines. Make the dialogue relevant to the topic. Keep it simple and friendly.`;

    setLoading(true);
    setDialogueCount(c => c + 1);
    askHaiku([{ role: "user", content: "Generate the practice dialogue now." }], sys)
      .then(txt => {
        const clean = txt.replace(/```json|```/g, "").trim();
        setDialogue(JSON.parse(clean));
      })
      .catch(() => setDialogue({
        lines: [
          { speaker: "A", text: displayWords[0]?.turkish || "Merhaba!", translation: displayWords[0]?.english || "Hello!" },
          { speaker: "B", text: displayWords[1]?.turkish || "Merhaba!", translation: displayWords[1]?.english || "Hello!" },
        ]
      }))
      .finally(() => setLoading(false));
  };

  const handleComplete = () => {
    // is_flashcard_eligible === false means "taught in this lesson" but not
    // "entered into spaced-repetition review" — the word still appears in
    // the lesson word list and examples above, it just never gets a card.
    const newCards = newWords
      .filter(w => w.is_flashcard_eligible !== false)
      .map(w => makeFSRSCard({
        ...w, topicOrder: topicData.order, topicName: topicData.topic, topicId: topicData.topic_id,
      }));
    const minutes = timerRef.current.getActiveMinutes();
    timerRef.current.stop();
    save(LESSON_POSITION_KEY, null);
    setDone(true);
    setTimeout(() => onComplete(newCards, minutes), 1200);
  };

  return (
    <div className="lesson-view">
      <div className="lesson-header">
        <button className="back-btn" onClick={onBack}>{t("lesson.backBtn")}</button>
        <div>
          <div className="lesson-title"><bdi>{getTranslatedTopicName(topicData, baseLanguage)}</bdi></div>
          <div style={{fontSize:"0.8125rem",color:"#8B7355"}}>{t("lesson.topicOf", { order: curriculum.findIndex(t => t.topic_id === topicData.topic_id) + 1, total: getTopicCount(langCode), words: topicWords.length })}</div>
        </div>
      </div>

      {translating && (
        <div style={{textAlign:"center",padding:"24px 0",color:"#8B7355"}}>
          <div className="spinner" style={{margin:"0 auto 10px"}} />
          <div style={{fontSize:"0.8125rem"}}>{t("lesson.translating", { lang: langName(baseLanguage) })}</div>
        </div>
      )}

      {!translating && trackWords.length > 0 && (
        <>
          <div className="lesson-progress-wrap">
            <div className="lesson-progress-label">
              {t("lesson.wordProgress", { current: currentIndex + 1, total: trackWords.length })}
            </div>
            <div className="lesson-progress-segments">
              {Array.from({ length: trackWords.length }).map((_, i) => (
                <div key={i} className={`lesson-progress-seg${i < currentIndex ? " lesson-progress-seg--done" : ""}${i === currentIndex ? " lesson-progress-seg--current" : ""}`} />
              ))}
            </div>
          </div>

          {currentIndex > 0 && (
            <div className="lesson-trail">
              {trackWords.slice(0, currentIndex).map(w => (
                peekingWordId === w.word_id ? (
                  <div key={w.id} className="trail-expanded-wrap" onClick={handlePeekCardClick}>
                    <VocabCard w={w} topic={topicData.topic} topicId={topicData.topic_id} baseLanguage={baseLanguage} speakLang={speakLang} langCode={langCode} />
                  </div>
                ) : (
                  <button key={w.id} type="button" className="trail-collapsed-row" onClick={() => togglePeek(w.word_id)}>
                    <span className="trail-collapsed-word"><bdi>{w.turkish}</bdi></span>
                    <span className="trail-collapsed-meaning">{w.meaning}</span>
                  </button>
                )
              ))}
            </div>
          )}

          {peekingWordId ? (
            <button type="button" className="trail-collapsed-row current-word-collapsed-row" onClick={() => setPeekingWordId(null)}>
              <span className="trail-collapsed-word"><bdi>{trackWords[currentIndex].turkish}</bdi></span>
              <span className="trail-collapsed-meaning">{trackWords[currentIndex].meaning}</span>
            </button>
          ) : (
            <div className="current-word-wrap" ref={currentCardRef}>
              <VocabCard key={trackWords[currentIndex].id} w={trackWords[currentIndex]} topic={topicData.topic} topicId={topicData.topic_id} baseLanguage={baseLanguage} speakLang={speakLang} langCode={langCode} />
            </div>
          )}

          <div className="lesson-nav-row">
            <button className="lesson-nav-btn lesson-nav-prev cta-btn" onClick={goPreviousWord} disabled={currentIndex === 0}>
              <span className="cta-arrow" aria-hidden="true">←</span><span>{t("lesson.previousWord")}</span>
            </button>
            <button className="lesson-nav-btn lesson-nav-next cta-btn" onClick={goNextWord} disabled={currentIndex >= trackWords.length - 1}>
              <span>{t("lesson.nextWord")}</span><span className="cta-arrow" aria-hidden="true">→</span>
            </button>
          </div>
        </>
      )}

      {!translating && newWords.length > 0 && reviewWords.length > 0 && (
        <>
          <div className="section-title" style={{marginTop:8}}>{t("lesson.alreadyLearned", { n: reviewWords.length })}</div>
          <div className="vocab-grid">
            {reviewWords.map(w => <VocabCard key={w.id} w={w} topic={topicData.topic} topicId={topicData.topic_id} baseLanguage={baseLanguage} speakLang={speakLang} langCode={langCode} dim />)}
          </div>
        </>
      )}

      {!translating && (
        <div className="dialogue-card" style={{marginTop:8}}>
          <div className="dialogue-title">{t("lesson.practiceDialogue")}</div>
          {dialogueCount === 0 && !loading ? (
            <div style={{textAlign:"center",padding:"8px 0 4px"}}>
              <button className="dlg-generate-btn" onClick={generateDialogue}>
                {t("lesson.generateDialogue")}
              </button>
              {/* Only once this topic is actually completed — Listen & Speak Lab only ever lets
                  a learner pick from the completedTopics ledger, so a deep link into a topic
                  still in progress here would have nothing to land on. */}
              {alreadyCompletedTopic && onGoToListenSpeak && (
                <div style={{marginTop:10}}>
                  <button
                    className="dlg-generate-btn dlg-listen-speak-btn"
                    onClick={() => onGoToListenSpeak(topicData.topic_id)}
                  >
                    {t("lesson.listenSpeakPractice")}
                  </button>
                </div>
              )}
            </div>
          ) : loading ? (
            <div style={{textAlign:"center",padding:24}}><div className="spinner" /><p style={{marginTop:12,fontSize:"0.8125rem",color:"#8B7355"}}>{t("lesson.generatingDialogue")}</p></div>
          ) : dialogue ? (
            <>
              {dialogue.lines.map((l, i) => (
                <div key={i} className={`dlg-line ${l.speaker==="A"?"right":""}`}>
                  <div className="dlg-bubble">
                    <div className="dlg-lbl">{l.speaker === "A" ? (profile?.name || "You") : "Babi"}</div>
                    <div className="dlg-text">
                      <bdi>{l.text}</bdi>
                      <button className="tts-btn" onClick={() => speak(l.text, null, speakLang)}>🔊</button>
                    </div>
                    <div className="dlg-tr" dir="auto">{l.translation}</div>
                  </div>
                </div>
              ))}
              {dialogueCount < 3 && (
                <div style={{textAlign:"right",marginTop:12}}>
                  <button className="dlg-regen-btn" onClick={generateDialogue}>{t("lesson.newDialogue")}</button>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {done ? (
        <div className="success-msg">{t("lesson.lessonSaved", { n: newWords.length })}</div>
      ) : newWords.length === 0 && alreadyCompletedTopic ? (
        <button className="primary-btn" disabled style={{marginTop:8,opacity:0.45,cursor:"not-allowed"}}>
          {t("lesson.alreadyTaken")}
        </button>
      ) : (
        <button className="primary-btn" onClick={handleComplete} style={{marginTop:8}}>
          {t("lesson.markComplete")}
        </button>
      )}
    </div>
  );
}

// Shared "Report Mistake" flow: idle -> AI auto-check -> optional human escalation, always
// writing to the same `reports` collection via saveReport(). Originally lived inline in
// VocabCard (lesson vocabulary); extracted so Grammar Lab's translated content can reuse the
// exact same state machine/UI instead of a parallel implementation. Content-specific pieces
// (the AI-check system prompt and the report payload) are supplied by the caller via
// buildCheckPrompt/buildReportPayload(origin, results) — origin is always 'auto-check' now (both
// call sites below pass it), so every report this flow writes shares one reportType regardless
// of whether the AI itself flagged the problem or the user overrode a clean AI check. `results`
// is always an array: Grammar Lab's buildCheckPrompt asks for one {confidence,suspicious,reason}
// object, which is normalized to a 1-element array here; VocabCard's asks for one such object
// per example slot (see buildMistakeCheckPrompt), and buildMistakeReportPayload zips those back
// onto sentence/translation text by slot to build exampleChecks. At most ONE report is ever
// written per instance: checkMistake writes one only when the AI flags a problem; escalateToHuman
// writes one only if checkMistake didn't already (see its own reportNeedsFix guard below) — if a
// report already exists for this instance, escalating just acknowledges that rather than writing
// a second one. The legacy top-level aiConfidence/aiReason always mirror results[0] — for
// VocabCard that's the example_1 slot, since the prompt lists it first and asks for matching order.
export function ReportMistakeFlow({ buildCheckPrompt, buildReportPayload }) {
  const { t } = useT();
  const [reportPhase, setReportPhase] = useState('idle');
  const [reportMessage, setReportMessage] = useState('');
  const [reportNeedsFix, setReportNeedsFix] = useState(false);
  const [reportEscalated, setReportEscalated] = useState(false);
  const [checkResults, setCheckResults] = useState([]);
  // Reentrancy guard for checkMistake, deliberately a ref rather than reportPhase state: a rapid
  // double-click/double-tap on the "Report mistake" button can fire two click events before
  // React commits the re-render that setReportPhase('checking') schedules, so a second
  // invocation's closure could still read reportPhase as 'idle' at that instant. A ref updates
  // synchronously and is shared across both invocations of this same component instance, so it
  // catches the second call even in that window — a state check alone would not.
  const isCheckingRef = useRef(false);

  const checkMistake = async () => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;
    setReportPhase('checking');
    try {
      const txt = await askClaude([{ role: "user", content: "Evaluate this translation." }], buildCheckPrompt());
      const parsed = JSON.parse(txt.replace(/```json|```/g, "").trim());
      const results = Array.isArray(parsed) ? parsed : [parsed];
      setCheckResults(results);
      const flagged = results.filter(r => r.suspicious || r.confidence < 80);
      const hasProblem = flagged.length > 0;
      setReportNeedsFix(hasProblem);
      const noIssueMessages = t("vocab.reportNoIssue");
      setReportMessage(hasProblem
        ? flagged.map(r => r.reason).filter(Boolean).join(" ")
        : noIssueMessages[Math.floor(Math.random() * noIssueMessages.length)]);
      if (hasProblem) {
        await saveReport({
          ...buildReportPayload('auto-check', results),
          aiConfidence: results[0]?.confidence,
          aiReason: results[0]?.reason,
          needsReview: true,
          createdAt: new Date().toISOString(),
          uid: auth?.currentUser?.uid || null,
          status: "open",
        });
      }
    } catch (_) {
      setReportNeedsFix(false);
      setCheckResults([]);
      setReportMessage(t("vocab.reportCheckError"));
    }
    setReportPhase('done');
    // Released here, not just on success — reportPhase leaving 'idle' is what hides this button
    // going forward, but if the user later clicks "OK, I understand" (reportPhase back to
    // 'idle') and then "Report mistake" again, that's a legitimate fresh check and must be
    // allowed to run, not permanently blocked by this guard.
    isCheckingRef.current = false;
  };

  const escalateToHuman = async () => {
    setReportEscalated(true);
    // checkMistake already saved a report for this instance whenever it found a problem — never
    // write a second one. reportNeedsFix is the source of truth for "a report already exists
    // here": checkMistake sets it true only in the branch where it also calls saveReport, and
    // resets it to false if the check itself failed (so a failed/never-saved auto-check still
    // lets the user's escalation through below, same as when the AI genuinely found nothing).
    if (reportNeedsFix) return;
    try {
      await saveReport({
        // Same reportType as checkMistake's own reports — by design nothing in the written data
        // distinguishes an AI-flagged report from a user-overridden one.
        ...buildReportPayload('auto-check', checkResults),
        aiConfidence: 100,
        aiReason: "User escalated despite no AI-detected issue",
        needsReview: true,
        createdAt: new Date().toISOString(),
        uid: auth?.currentUser?.uid || null,
        status: "open",
      });
    } catch (_) {}
  };

  return (
    <div className="report-wrap">
      {reportPhase === 'idle' && (
        <button className="report-link" onClick={checkMistake}>
          {t("vocab.reportMistake")}
        </button>
      )}
      {(reportPhase === 'checking' || reportPhase === 'done') && (
        <div className="babi-checker">
          <img className="babi-checker-avatar" src="/babi%20fantasy2.jpg" alt="Babi" />
          <div className="babi-checker-bubble">
            {reportPhase === 'checking' ? (
              <div className="babi-checker-loading">
                <div className="spinner" style={{width:16,height:16,borderWidth:'2px',margin:0}} />
                <span>{t("vocab.reportChecking")}</span>
              </div>
            ) : (
              <>
                <div className="babi-checker-msg">{reportMessage}</div>
                {!reportEscalated ? (
                  <div className="babi-checker-btns">
                    <button className="babi-checker-ok" onClick={() => setReportPhase('idle')}>{t("vocab.reportOkBtn")}</button>
                    <button className="babi-checker-escalate" onClick={escalateToHuman}>{t("vocab.reportEscalateBtn")}</button>
                  </div>
                ) : (
                  <div className="babi-checker-escalated">
                    {reportNeedsFix ? t("vocab.reportAlreadyFlaggedMsg") : t("vocab.reportEscalatedMsg")}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function VocabCard({ w, dim, topic, topicId, baseLanguage = "English", speakLang = "tr", langCode = "turkish" }) {
  const { t } = useT();
  const analysisKey = `analysis_${w.id || w.turkish}`;
  const origEx   = w.ex || w.example || null;
  const origExTr = w.exTr || w.example_translation || null;

  // "Another Example" now walks a fixed, deterministic sequence through curriculum.js's
  // pre-written example_2/example_3 (previously: an unbounded Haiku call generating a fresh
  // throwaway sentence per click). exampleClickState is plain component state, not persisted —
  // VocabCard is remounted with a fresh key per word in LessonView (see trackWords[currentIndex]
  // key usage), so navigating away and back to a word always resets to showing only example_1,
  // by construction rather than by explicit reset code. 0 = only example_1 shown, 1 = +example_2,
  // 2 = +example_3 (MAX_EXAMPLES also caps here, same constant as before).
  const [exampleClickState, setExampleClickState] = useState(0);
  // Starts closed on every mount (VocabCard remounts per word, same as exampleClickState above) —
  // fetchAnalysis still checks the localStorage cache first, so a previously-analyzed word shows
  // its result instantly on click without a repeat AI call, but the box no longer reopens itself
  // just because the word was analyzed on some earlier visit, possibly days ago.
  const [analysis, setAnalysis] = useState(null);
  // Tracks whether `analysis` currently holds the translated error message rather than a real
  // AI analysis — errors are never cached (see the catch branch below), so this always starts
  // false on mount/reload; a cached `analysis` is guaranteed to be a real prior success.
  const [analysisError, setAnalysisError] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [animatingStar, setAnimatingStar] = useState(null);
  const prevClickState = useRef(0);

  useEffect(() => {
    if (exampleClickState > prevClickState.current) {
      setAnimatingStar(exampleClickState);
      const timer = setTimeout(() => setAnimatingStar(null), 1100);
      prevClickState.current = exampleClickState;
      return () => clearTimeout(timer);
    }
    prevClickState.current = exampleClickState;
  }, [exampleClickState]);

  // example_2/example_3 Turkish text always exists in curriculum.js for every word; their
  // translated fields (example_2_translation/example_3_translation) only exist in the cache for
  // English/Farsi so far. For any other baseLanguage, allowGlossFallback tells the render below
  // to show the word's own (already-localized) meaning instead of leaving the line blank —
  // same "show something rather than nothing" idea as getTranslatedTopicName's English fallback.
  const extraExamples = [
    exampleClickState >= 1 && w.example_2 ? { ex: w.example_2, exTr: w.example_2_translation || null, allowGlossFallback: true } : null,
    exampleClickState >= 2 && w.example_3 ? { ex: w.example_3, exTr: w.example_3_translation || null, allowGlossFallback: true } : null,
  ].filter(Boolean);

  const allExamples = origEx
    ? [{ ex: origEx, exTr: origExTr, allowGlossFallback: false }, ...extraExamples]
    : [...extraExamples];
  const canShowMore = exampleClickState < MAX_EXAMPLES;

  const showNextExample = () => {
    if (!canShowMore) return;
    setExampleClickState(s => Math.min(s + 1, MAX_EXAMPLES));
  };

  // "example_1" is the card's original stored example; "example_2"/"example_3" are the
  // curriculum's pre-written follow-up sentences, shown only once the learner has actually
  // clicked through to them (mirrors what's currently on screen, same as the old
  // generatedExamples-based version of this list).
  const buildExampleSlots = () => allExamples.map((e, i) => ({
    slot: i === 0 ? "example_1" : i === 1 ? "example_2" : "example_3",
    ex: e.ex, exTr: e.exTr,
  }));

  const buildMistakeCheckPrompt = () => {
    const slots = buildExampleSlots();
    const slotLines = slots.map(s =>
      `- slot "${s.slot}": Turkish sentence: "${s.ex}" | ${baseLanguage} translation: "${s.exTr}"`
    ).join("\n");
    return `You are a Turkish language translation quality evaluator. Evaluate the accuracy of the following word translation and each example sentence/translation pair for that word.

Word (Turkish): "${w.turkish}"
Word meaning (${baseLanguage}): "${w.meaning || w.english || ""}"

Example sentences:
${slotLines}

For each slot listed above, assess whether its Turkish sentence and ${baseLanguage} translation are accurate and natural for this word. If you find any issue with a slot, describe it briefly in one sentence for that slot alone, written in ${baseLanguage}. Respond ONLY with valid JSON, no markdown — an array with exactly one object per slot, in the same order as listed above: [{"slot":"example_1","confidence":0-100,"suspicious":true/false,"reason":"one sentence, in ${baseLanguage}"}]`;
  };

  const buildMistakeReportPayload = (origin, results = []) => {
    const slots = buildExampleSlots();
    const bySlot = new Map(results.map(r => [r.slot, r]));
    const exampleChecks = slots.map(s => {
      const r = bySlot.get(s.slot) || {};
      return {
        slot: s.slot,
        sentenceText: s.ex,
        translationText: s.exTr,
        aiConfidence: r.confidence,
        aiSuspicious: !!r.suspicious,
        aiReason: r.reason || "",
      };
    });
    return {
      word: w.turkish,
      meaning: w.meaning || w.english || "",
      // Legacy flat fields, kept for any other code / already-open admin sessions still
      // reading the old shape — always the example_1 slot, same as before this change.
      example: origEx || "",
      exampleTranslation: origExTr || "",
      exampleChecks,
      reportType: origin,
      // Structured identifiers, additive alongside the free-text fields above — let a future
      // correction feature programmatically locate the exact source row instead of relying on
      // text matching (which is ambiguous: some Turkish surface forms map to multiple word_ids,
      // and the same word_id's example text can differ per topic). Mirrors the grammarPointId
      // pattern Grammar Lab's own report path already uses.
      word_id: w.word_id,
      topic_id: topicId,
      // So an admin fixing this report's content applies the correction under the right
      // language's curriculum_corrections bucket instead of defaulting to Turkish.
      langCode,
    };
  };

  const fetchAnalysis = async () => {
    if (analysis) return;
    const cached = load(analysisKey);
    if (cached) { setAnalysis(cached); return; }
    setLoadingAnalysis(true);
    setAnalysisError(false);
    // The lead-in sentence shown above this content is a translated UI string
    // (t("vocab.analysisIntro")), not AI output — asking the model to write it used to bake a
    // hardcoded English sentence into every response regardless of baseLanguage. Instead, the
    // model is told to skip any greeting/lead-in and start directly with the breakdown.
    const sys = `You are a Turkish grammar expert and teacher. The learner's language is ${baseLanguage}. Analyze the word or phrase "${w.turkish}" step by step. Break it into every morphological part. For each part write one line in this exact format: [part]: Turkish grammar term (English term) - explanation in ${baseLanguage}. Technical labels inside [brackets] may stay in English. Do not include any greeting, lead-in sentence, or summary — start your response directly with the first [part] line and end after the last one.`;
    try {
      const txt = await askClaude([{ role: "user", content: "Analyze this word grammatically." }], sys);
      setAnalysis(txt);
      save(analysisKey, txt);
    } catch (_) {
      setAnalysisError(true);
      setAnalysis(t("vocab.analysisError"));
    } finally { setLoadingAnalysis(false); }
  };

  const exLeft = MAX_EXAMPLES - exampleClickState;
  const exLabel = !canShowMore
    ? t("vocab.limitReached")
    : exampleClickState > 0
      ? t("vocab.anotherLeft", { n: exLeft })
      : t("vocab.anotherExample");
  const analyzeLabel = loadingAnalysis
    ? t("vocab.analyzing")
    : analysis
      ? t("vocab.analyzed")
      : t("vocab.analyzeWord");

  const filledStars = Math.min(1 + exampleClickState, 3);

  return (
    <div className="vocab-card" style={dim ? { opacity: 0.7 } : {}}>
      <div className="word-stars">
        {[0, 1, 2].map(i => (
          <span key={i} className={`word-star${i < filledStars ? ' word-star-filled' : ' word-star-empty'}${animatingStar === i ? ' word-star-anim' : ''}`}>
            {i < filledStars ? '★' : '☆'}
          </span>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div className="v-turkish"><bdi>{w.turkish}</bdi></div>
        <button className="tts-btn" onClick={() => speak(w.turkish, w.word_id, speakLang, 'word')}>🔊</button>
      </div>
      <div className="v-meaning" dir="auto">{w.meaning}</div>
      {(w.pron || w.ipa) && <div className="v-pron">{w.pron}{w.pron && w.ipa ? " · " : ""}<em>{w.ipa}</em></div>}
      {w.morph && (
        <div className="morph-wrap">
          <div className="morph-label">{t("vocab.structure")}</div>
          <div className="morph-row">
            {w.morph.map((part, i) => (
              <span key={i} style={{display:"flex",alignItems:"center",gap:3}}>
                {i > 0 && <span className="morph-sep">+</span>}
                <span className={`morph-chip morph-${part.role}`} title={part.gloss}>{part.text}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {allExamples.map((e, i) => (
        <div key={i} className="v-ex" style={i > 0 ? { marginTop: 8, paddingTop: 8, borderTop: "1px solid #F0DFC0" } : {}}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
            <em dir="auto">{e.ex}</em>
            <button className="tts-btn" onClick={() => speak(e.ex, w.word_id, speakLang, `ex${i + 1}`)}>🔊</button>
          </div>
          {e.exTr
            ? <div className="v-extr" dir="auto">{e.exTr}</div>
            : (e.allowGlossFallback && w.meaning) ? <div className="v-extr" dir="auto">{w.meaning}</div> : null}
        </div>
      ))}
      <div className="vocab-actions">
        <button className="vocab-action-btn" onClick={showNextExample} disabled={!canShowMore}>
          {exLabel}
        </button>
        <button className="vocab-action-btn vocab-action-accent" onClick={fetchAnalysis} disabled={loadingAnalysis || !!analysis}>
          {analyzeLabel}
        </button>
      </div>
      {analysis && (
        <div className="vocab-analysis">
          {!analysisError && <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("vocab.analysisIntro")}</div>}
          {analysis}
        </div>
      )}
      <ReportMistakeFlow
        buildCheckPrompt={buildMistakeCheckPrompt}
        buildReportPayload={buildMistakeReportPayload}
      />
    </div>
  );
}
